const AIRTABLE_BASE_ID =
  process.env.REACT_APP_AIRTABLE_BASE_ID || 'app2fS2uv1DP0TJ1n';
const AIRTABLE_TOKEN =
  process.env.REACT_APP_AIRTABLE_TOKEN ||
  'patHDjQ3LCeHwzs3r.c4f1d618e9f4cc2a85229da3037504e11e816f47ee8d226eb45ba14cd68d81e3';

// v2: bumped when "Parking Pass Number" was added so browsers holding a
// pre-pass-number cache discard it and refetch instead of silently missing the field.
const CACHE_KEY = 'vehicle_cache_v2';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

/** Strip punctuation/spaces and upper-case so "ckk-0002", "CKK 0002" and "CKK0002" all match. */
const normalize = (s) => (s ?? '').toString().toUpperCase().replace(/[^A-Z0-9]/g, '');

class AirtableService {
  constructor() {
    this.baseUrl = `https://api.airtable.com/v0/${AIRTABLE_BASE_ID}`;
    this.headers = {
      Authorization: `Bearer ${AIRTABLE_TOKEN}`,
      'Content-Type': 'application/json'
    };
    this.vehicleCache = null;
    this.cacheTimestamp = null;
  }

  // ── Core helpers ──────────────────────────────────────────────────────────────

  async makeRequest(endpoint, options = {}) {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        headers: this.headers,
        ...options
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        const message =
          data?.error?.message ||
          data?.message ||
          `HTTP error! status: ${response.status}`;
        throw new Error(message);
      }
      return data;
    } catch (error) {
      console.error('Error making request to Airtable:', error);
      throw error;
    }
  }

  async getAllRecords(endpoint) {
    let allRecords = [];
    let offset = null;
    try {
      do {
        let url = `${this.baseUrl}${endpoint}`;
        const params = new URLSearchParams();
        if (endpoint.includes('?')) {
          const [baseEndpoint, queryString] = endpoint.split('?');
          url = `${this.baseUrl}${baseEndpoint}`;
          new URLSearchParams(queryString).forEach((v, k) => params.append(k, v));
        }
        if (offset) params.append('offset', offset);
        params.append('pageSize', '100');
        const fullUrl = params.toString() ? `${url}?${params.toString()}` : url;
        const response = await fetch(fullUrl, { headers: this.headers });
        const data = await response.json().catch(() => null);
        if (!response.ok) throw new Error(data?.error?.message ?? `HTTP error! status: ${response.status}`);
        if (data?.records) allRecords = allRecords.concat(data.records);
        offset = data?.offset ?? null;
      } while (offset);
      return { records: allRecords };
    } catch (error) {
      console.error('Error getting all records:', error);
      return null;
    }
  }

  // ── Vehicle cache persistence (localStorage) ──────────────────────────────────

  restoreCacheFromStorage() {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      this.vehicleCache = parsed.records;
      this.cacheTimestamp = new Date(parsed.timestamp);
    } catch (e) {
      console.warn('Could not restore vehicle cache:', e);
    }
  }

  persistCacheToStorage() {
    if (!this.vehicleCache || !this.cacheTimestamp) return;
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        records: this.vehicleCache,
        timestamp: this.cacheTimestamp.toISOString()
      }));
    } catch (e) {
      console.warn('Could not persist vehicle cache:', e);
    }
  }

  get cacheIsStale() {
    if (!this.cacheTimestamp) return true;
    return Date.now() - this.cacheTimestamp.getTime() > CACHE_TTL_MS;
  }

  /**
   * Load the vehicle database.
   * - Instantly restores from localStorage (works across page refreshes).
   * - Silently refreshes from Airtable when the cache is >24 h old.
   */
  async loadVehicleDatabase() {
    if (!this.vehicleCache) {
      this.restoreCacheFromStorage();
    }
    if (this.vehicleCache && !this.cacheIsStale) {
      return true;
    }
    try {
      const response = await this.getAllRecords('/Vehicles');
      if (response?.records?.length) {
        this.vehicleCache = response.records;
        this.cacheTimestamp = new Date();
        this.persistCacheToStorage();
        console.log(`✅ Vehicle cache refreshed: ${response.records.length} records`);
      }
    } catch (error) {
      console.warn('Could not refresh vehicle cache:', error);
    }
    return !!this.vehicleCache;
  }

  // ── Vehicle search ────────────────────────────────────────────────────────────

  async searchVehicle(licensePlate) {
    const query = normalize(licensePlate);
    if (!query) return null;
    if (this.vehicleCache) {
      return this.vehicleCache.find(
        v => normalize(v.fields['License Plate']) === query
      ) ?? null;
    }
    try {
      const formula = `UPPER(SUBSTITUTE(SUBSTITUTE({License Plate}," ",""),"-","")) = "${query}"`;
      const response = await this.makeRequest(
        `/Vehicles?filterByFormula=${encodeURIComponent(formula)}`
      );
      return response?.records?.[0] ?? null;
    } catch (error) {
      console.error('Error searching vehicle by plate:', error);
      return null;
    }
  }

  /**
   * Search by parking pass number (e.g. CKK-0002, MVP-0024).
   * Hyphens and spaces are ignored, so "mvp24" will not match but
   * "mvp-0024", "MVP 0024" and "MVP0024" all will.
   */
  async searchVehicleByPass(passNumber) {
    const query = normalize(passNumber);
    if (!query) return null;
    if (this.vehicleCache) {
      return this.vehicleCache.find(
        v => normalize(v.fields['Parking Pass Number']) === query
      ) ?? null;
    }
    try {
      const formula = `UPPER(SUBSTITUTE(SUBSTITUTE({Parking Pass Number}," ",""),"-","")) = "${query}"`;
      const response = await this.makeRequest(
        `/Vehicles?filterByFormula=${encodeURIComponent(formula)}`
      );
      return response?.records?.[0] ?? null;
    } catch (error) {
      console.error('Error searching vehicle by parking pass:', error);
      return null;
    }
  }

  getCacheInfo() {
    if (!this.vehicleCache || !this.cacheTimestamp) return null;
    return {
      vehicleCount: this.vehicleCache.length,
      lastUpdated: this.cacheTimestamp,
      isStale: this.cacheIsStale
    };
  }

  // ── Visitor operations ────────────────────────────────────────────────────────

  async checkInVisitor(visitorData) {
    try {
      const response = await this.makeRequest('/Visitors', {
        method: 'POST',
        body: JSON.stringify({
          fields: {
            Name: visitorData.name,
            'Reason for Visit': visitorData.reason,
            User: visitorData.user,
            Status: 'Checked In'
          }
        })
      });
      return response !== null;
    } catch (error) {
      console.error('Error checking in visitor:', error);
      return false;
    }
  }

  async getActiveVisitors() {
    try {
      const filter = encodeURIComponent(`{Status} = "Checked In"`);
      const response = await this.getAllRecords(
        `/Visitors?filterByFormula=${filter}&sort%5B0%5D%5Bfield%5D=Check%20In%20Time&sort%5B0%5D%5Bdirection%5D=desc`
      );
      return response?.records ?? [];
    } catch (error) {
      console.error('Error getting active visitors:', error);
      return [];
    }
  }

  async checkOutVisitor(recordId) {
    try {
      const response = await this.makeRequest(`/Visitors/${recordId}`, {
        method: 'PATCH',
        body: JSON.stringify({ fields: { Status: 'Checked Out' } })
      });
      return response !== null;
    } catch (error) {
      console.error('Error checking out visitor:', error);
      return false;
    }
  }

  async getAllVisitors() {
    try {
      const response = await this.getAllRecords(
        '/Visitors?sort%5B0%5D%5Bfield%5D=Check%20In%20Time&sort%5B0%5D%5Bdirection%5D=desc'
      );
      return response?.records ?? [];
    } catch (error) {
      console.error('Error getting all visitors:', error);
      return [];
    }
  }

  // ── Perimeter check operations ────────────────────────────────────────────────

  async getPerimeterChecks() {
    try {
      const response = await this.getAllRecords(
        '/Perimeter%20Checks?sort%5B0%5D%5Bfield%5D=Check%20Time&sort%5B0%5D%5Bdirection%5D=desc'
      );
      return response?.records ?? [];
    } catch (error) {
      console.error('Error getting perimeter checks:', error);
      return [];
    }
  }

  async createPerimeterCheck(checkData) {
    try {
      const response = await this.makeRequest('/Perimeter%20Checks', {
        method: 'POST',
        body: JSON.stringify({
          fields: {
            'Security Guard Name': checkData.officer,
            'Completion Status': checkData.status,
            Notes: checkData.notes || ''
          }
        })
      });
      return response !== null;
    } catch (error) {
      console.error('Error creating perimeter check:', error);
      return false;
    }
  }

  // ── Incident operations ───────────────────────────────────────────────────────

  async createIncident(incidentData) {
    if (!incidentData?.location?.trim()) throw new Error('Location is required');
    if (!incidentData?.description?.trim()) throw new Error('Description is required');
    if (!incidentData?.guard?.trim()) throw new Error('Guard is required');
    if (!incidentData?.priority?.trim()) throw new Error('Priority is required');

    await this.makeRequest('/Incidents', {
      method: 'POST',
      body: JSON.stringify({
        fields: {
          'Date/Time': new Date().toISOString(),
          Location: incidentData.location.trim(),
          Description: incidentData.description.trim(),
          Priority: incidentData.priority.trim(),
          Guard: incidentData.guard.trim()
        }
      })
    });
    return true;
  }

  async getIncidents() {
    try {
      const response = await this.getAllRecords(
        '/Incidents?sort%5B0%5D%5Bfield%5D=Date%2FTime&sort%5B0%5D%5Bdirection%5D=desc'
      );
      return response?.records ?? [];
    } catch (error) {
      console.error('Error getting incidents:', error);
      return [];
    }
  }

  // ── Unauthorized vehicle operations ───────────────────────────────────────────

  async reportUnauthorizedVehicle(data) {
    try {
      await this.makeRequest('/Unauthorized%20Vehicles', {
        method: 'POST',
        body: JSON.stringify({
          fields: {
            'License Plate': data.licensePlate.trim().toUpperCase(),
            Location: data.location.trim(),
            'Reported By': data.reportedBy,
            'Date/Time': new Date().toISOString(),
            Status: 'Active',
            Notes: data.notes?.trim() ?? ''
          }
        })
      });
      return true;
    } catch (error) {
      console.error('Error reporting unauthorized vehicle:', error);
      return false;
    }
  }

  async getUnauthorizedVehicles() {
    try {
      const filter = encodeURIComponent(`{Status} = "Active"`);
      const sort = 'sort%5B0%5D%5Bfield%5D=Date%2FTime&sort%5B0%5D%5Bdirection%5D=desc';
      const response = await this.getAllRecords(
        `/Unauthorized%20Vehicles?filterByFormula=${filter}&${sort}`
      );
      return response?.records ?? [];
    } catch (error) {
      console.error('Error getting unauthorized vehicles:', error);
      return [];
    }
  }

  async clearUnauthorizedVehicle(recordId, licensePlate) {
    try {
      const authorized = await this.searchVehicle(licensePlate);
      if (!authorized) return 'not-authorized';
      await this.makeRequest(`/Unauthorized%20Vehicles/${recordId}`, {
        method: 'PATCH',
        body: JSON.stringify({ fields: { Status: 'Cleared' } })
      });
      return 'cleared';
    } catch (error) {
      console.error('Error clearing unauthorized vehicle:', error);
      return 'error';
    }
  }
}

export const airtableService = new AirtableService();
